import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { QRCodeCanvas } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { useLanguage } from '../contexts/LanguageContext';
import { BACKEND_URL } from '../config';
import { parseNumberInput, getNumberInputValue } from '../utils/numberInputHelpers';
import ProductFeaturedControls from './ProductFeaturedControls';

const FONT_OPTIONS = [
  { value: '', label: 'System Default' },
  { value: "'Roboto', sans-serif", label: 'Roboto' },
  { value: "'Open Sans', sans-serif", label: 'Open Sans' },
  { value: "'Lato', sans-serif", label: 'Lato' },
  { value: "'Playfair Display', serif", label: 'Playfair Display' },
  { value: "'Lora', serif", label: 'Lora' },
  { value: "'Merriweather', serif", label: 'Merriweather' },
  { value: "'Montserrat', sans-serif", label: 'Montserrat' },
  { value: "'Raleway', sans-serif", label: 'Raleway' },
];

const COLUMN_OPTIONS = [
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
];

// Defined at module level so its reference is stable across renders.
// Defining it inside the component body would create a new type on every render,
// causing React to unmount/remount the entire subtree on each state update.
const CollapsibleSection = ({ title, isOpen, onToggle, theme, children }) => (
  <div style={{
    background: theme?.colors?.surface || '#fff',
    borderRadius: theme?.borderRadius || 8,
    boxShadow: theme?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: '1.5rem',
    overflow: 'hidden',
  }}>
    <div
      onClick={onToggle}
      onTouchEnd={(e) => { e.preventDefault(); onToggle(); }}
      style={{
        padding: '.5rem 1.5rem',
        backgroundColor: theme?.colors?.primary || '#1976d2',
        color: theme?.colors?.surface || '#fff',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontWeight: 'bold',
        fontSize: '1.1rem',
        WebkitTapHighlightColor: 'rgba(0,0,0,0)',
        userSelect: 'none',
      }}
    >
      <span>{title}</span>
      <span style={{ fontSize: '1.2rem' }}>{isOpen ? '▼' : '▶'}</span>
    </div>
    {isOpen && (
      <div style={{ padding: '1.5rem' }}>
        {children}
      </div>
    )}
  </div>
);

const LayoutIcon = ({ type, color, borderColor }) => {
  const block = (w, h) => ({
    backgroundColor: color, borderRadius: 2, width: w, height: h, flexShrink: 0,
  });
  const wrap = { width: 50, height: 40, display: 'flex', gap: 2, padding: 2,
    border: `1px solid ${borderColor}`, borderRadius: 3, marginBottom: 4 };

  if (type === 'grid') return (
    <div style={{ ...wrap, flexWrap: 'wrap' }}>
      <div style={block(22, 17)} /><div style={block(22, 17)} />
      <div style={block(22, 17)} /><div style={block(22, 17)} />
    </div>
  );
  if (type === 'flow') return (
    <div style={{ ...wrap, flexDirection: 'column' }}>
      <div style={block(44, 14)} />
      <div style={{ display: 'flex', gap: 2, flex: 1 }}>
        <div style={block(21, 20)} /><div style={block(21, 20)} />
      </div>
    </div>
  );
  if (type === 'compact') return (
    <div style={{ ...wrap }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        <div style={block('100%', 22)} /><div style={block('100%', 10)} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        <div style={block('100%', 12)} /><div style={block('100%', 20)} />
      </div>
    </div>
  );
  // masonry
  return (
    <div style={{ ...wrap }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
        <div style={block('100%', 20)} /><div style={block('100%', 10)} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, marginTop: 6 }}>
        <div style={block('100%', 14)} /><div style={block('100%', 16)} />
      </div>
    </div>
  );
};

const LAYOUT_OPTIONS = [
  {
    value: 'grid',
    label: 'Grid',
    desc: 'Each category stays in its own column block. Equal row heights.',
  },
  {
    value: 'flow',
    label: 'Flow',
    desc: 'Large categories span multiple columns. Small ones fill single cells.',
  },
  {
    value: 'compact',
    label: 'Compact',
    desc: 'Categories pre-sorted into columns by size. No equal row heights.',
  },
  {
    value: 'masonry',
    label: 'Masonry',
    desc: 'Items flow freely across column lines like newspaper columns.',
  },
];

// Logo target widths in mm for PDF, and px for preview (at ~2.5px/mm scale).
// Heights are NOT used for PDF — the actual aspect ratio is measured at download time.
// Heights here are only used as a bounding-box hint for the preview.
const LOGO_DIMS = {
  small:  { mm: [32, 16], px: [80,  40] },
  medium: { mm: [48, 24], px: [120, 60] },
  large:  { mm: [72, 36], px: [180, 90] },
};

function DigitalMenuSettings({ theme, onBrandingChange }) {
  const { t, isSpanish } = useLanguage();

  // Settings state
  const [menuEnabled, setMenuEnabled] = useState(false);
  const [menuMode, setMenuMode] = useState('live');
  const [menuColumns, setMenuColumns] = useState('1');
  const [menuColumnsMobile, setMenuColumnsMobile] = useState('');
  const [menuColumnsSmall, setMenuColumnsSmall] = useState('');
  const [menuLayout, setMenuLayout] = useState('grid');
  const [menuLayoutThreshold, setMenuLayoutThreshold] = useState(5);
  const [menuFont, setMenuFont] = useState('');
  const [menuBgColor, setMenuBgColor] = useState('#f9f9f9');
  const [menuTextColor, setMenuTextColor] = useState('#1a1a1a');
  const [menuHeaderColor, setMenuHeaderColor] = useState('#333333');
  const [menuAccentColor, setMenuAccentColor] = useState('#1976d2');
  const [menuAccentOpacity, setMenuAccentOpacity] = useState(7);
  const [menuLanguageToggle, setMenuLanguageToggle] = useState(false);
  const [menuDefaultLanguage, setMenuDefaultLanguage] = useState('en');

  // Category groups & display options
  const [menuCategoryGroups, setMenuCategoryGroups] = useState([]);
  const [menuShowTitle, setMenuShowTitle] = useState(true);
  const [menuShowNames, setMenuShowNames] = useState('both');
  const [menuShowItemImages, setMenuShowItemImages] = useState(true);

  // Categories with visibility + order
  const [categories, setCategories] = useState([]);

  // Menu logo display size
  const [menuLogoSize, setMenuLogoSize] = useState('medium');
  const [menuLogoCustomWidth, setMenuLogoCustomWidth] = useState(80);

  // Logo state
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [currentLogoUrl, setCurrentLogoUrl] = useState('');

  // QR PDF settings
  const [qrIncludeLogo, setQrIncludeLogo] = useState(true);
  const [qrCustomTextBold, setQrCustomTextBold] = useState(false);
  const [qrCustomTextItalic, setQrCustomTextItalic] = useState(false);
  const [savedQrForPreview, setSavedQrForPreview] = useState(null);
  const [qrLogoSize, setQrLogoSize] = useState('medium');
  const [qrLogoPlacement, setQrLogoPlacement] = useState('above');
  const [qrIncludeUrl, setQrIncludeUrl] = useState(true);
  const [qrCustomText, setQrCustomText] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(null); // null | 'en' | 'es'
  const [loadError, setLoadError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [snapshotStatus, setSnapshotStatus] = useState('');
  const [snapshotDate, setSnapshotDate] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({}); // group id -> bool
  const [dragOverGroupId, setDragOverGroupId] = useState(null); // group id or '__ungrouped__'
  const [dragOverCatId, setDragOverCatId] = useState(null); // category id being hovered
  const [dragOverHalf, setDragOverHalf] = useState(null); // 'top' or 'bottom'
  const dragCatIdRef = useRef(null); // the category being dragged

  const autoSaveTimer = useRef(null);
  const isInitialLoad = useRef(true);

  // Collapsible section state
  const [showMenuSettings, setShowMenuSettings] = useState(false);
  const [showLogo, setShowLogo] = useState(false);
  const [showColorsFont, setShowColorsFont] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [showPhotos, setShowPhotos] = useState(false);
  const [showLanguageToggle, setShowLanguageToggle] = useState(false);
  const [showCategoryVisibility, setShowCategoryVisibility] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);
  const [showMenuPreview, setShowMenuPreview] = useState(false);

  // Hidden high-res QR canvas for PDF extraction
  const qrRef = useRef(null);
  const menuUrl = `${window.location.protocol}//${window.location.host}/menu`;

  // Load Google Fonts dynamically
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Roboto&family=Open+Sans&family=Lato&family=Playfair+Display&family=Lora&family=Merriweather&family=Montserrat&family=Raleway&display=swap';
    document.head.appendChild(link);
    return () => { try { document.head.removeChild(link); } catch (e) { /* already removed */ } };
  }, []);

  const loadSettings = useCallback(async () => {
    isInitialLoad.current = true;
    try {
      setLoading(true);
      setLoadError('');
      const res = await fetch(`${BACKEND_URL}/api/menu/settings`); // eslint-disable-line
      if (!res.ok) {
        const errText = await res.text().catch(() => res.status);
        throw new Error(`HTTP ${res.status}: ${errText}`);
      }
      const data = await res.json();
      console.log('[DigitalMenuSettings] loaded categories:', data.categories?.length);
      setMenuEnabled(!!data.menuEnabled);
      setMenuMode(data.menuMode || 'live');
      setMenuColumns(data.menuColumns || '1');
      setMenuColumnsMobile(data.menuColumnsMobile || '');
      setMenuColumnsSmall(data.menuColumnsSmall || '');
      setMenuLayout(data.menuLayout || 'grid');
      setMenuLayoutThreshold(data.menuLayoutThreshold ?? 5);
      setMenuLogoSize(data.menuLogoSize || 'medium');
      setMenuLogoCustomWidth(data.menuLogoCustomWidth ?? 80);
      setMenuFont(data.menuFont || '');
      setMenuBgColor(data.menuBgColor || '#f9f9f9');
      setMenuTextColor(data.menuTextColor || '#1a1a1a');
      setMenuHeaderColor(data.menuHeaderColor || '#333333');
      setMenuAccentColor(data.menuAccentColor || '#1976d2');
      setMenuAccentOpacity(data.menuAccentOpacity ?? 7);
      setMenuLanguageToggle(!!data.menuLanguageToggle);
      setMenuDefaultLanguage(data.menuDefaultLanguage || 'en');
      setMenuCategoryGroups(data.menuCategoryGroups || []);
      setMenuShowTitle(data.menuShowTitle !== false);
      setMenuShowNames(data.menuShowNames || 'both');
      setMenuShowItemImages(data.menuShowItemImages !== false);
      setCategories(data.categories || []);
      setQrIncludeLogo(data.qrIncludeLogo !== false);
      setQrLogoSize(data.qrLogoSize || 'medium');
      setQrLogoPlacement(data.qrLogoPlacement || 'above');
      setQrIncludeUrl(data.qrIncludeUrl !== false);
      setQrCustomText(data.qrCustomText || '');
      setQrCustomTextBold(!!data.qrCustomTextBold);
      setQrCustomTextItalic(!!data.qrCustomTextItalic);
      setSavedQrForPreview({
        qrIncludeLogo: data.qrIncludeLogo !== false,
        qrLogoSize: data.qrLogoSize || 'medium',
        qrLogoPlacement: data.qrLogoPlacement || 'above',
        qrIncludeUrl: data.qrIncludeUrl !== false,
        qrCustomText: data.qrCustomText || '',
        qrCustomTextBold: !!data.qrCustomTextBold,
        qrCustomTextItalic: !!data.qrCustomTextItalic,
      });
      isInitialLoad.current = false;
    } catch (err) {
      console.error('Error loading menu settings:', err);
      setLoadError(err.message || 'Failed to load settings');
      isInitialLoad.current = false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
    fetch(`${BACKEND_URL}/api/branding/logo`)
      .then(r => r.ok ? setCurrentLogoUrl(`${BACKEND_URL}/api/branding/logo?t=${Date.now()}`) : null)
      .catch(() => null);
  }, [loadSettings]);

  // ── AUTO-SAVE ─────────────────────────────────────────────────────────────
  const saveSettings = useCallback(async (payload) => {
    try {
      setSaveStatus('saving');
      const res = await fetch(`${BACKEND_URL}/api/menu/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(''), 2500);
        setSavedQrForPreview({
          qrIncludeLogo: payload.qrIncludeLogo,
          qrLogoSize: payload.qrLogoSize,
          qrLogoPlacement: payload.qrLogoPlacement,
          qrIncludeUrl: payload.qrIncludeUrl,
          qrCustomText: payload.qrCustomText,
          qrCustomTextBold: payload.qrCustomTextBold,
          qrCustomTextItalic: payload.qrCustomTextItalic,
        });
      } else {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(''), 3000);
      }
    } catch (err) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  }, []);

  useEffect(() => {
    if (isInitialLoad.current) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      saveSettings({
        menuEnabled, menuMode, menuColumns, menuColumnsMobile, menuColumnsSmall, menuLayout, menuLayoutThreshold, menuLogoSize, menuLogoCustomWidth, menuFont,
        menuBgColor, menuTextColor, menuHeaderColor, menuAccentColor, menuAccentOpacity,
        menuLanguageToggle, menuDefaultLanguage,
        menuCategoryGroups, menuShowTitle, menuShowNames, menuShowItemImages,
        categories,
        qrIncludeLogo, qrLogoSize, qrLogoPlacement, qrIncludeUrl, qrCustomText, qrCustomTextBold, qrCustomTextItalic,
      });
    }, 1200);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [
    menuEnabled, menuMode, menuColumns, menuColumnsMobile, menuColumnsSmall, menuLayout, menuLayoutThreshold, menuLogoSize, menuLogoCustomWidth, menuFont,
    menuBgColor, menuTextColor, menuHeaderColor, menuAccentColor, menuAccentOpacity,
    menuLanguageToggle, menuDefaultLanguage,
    menuCategoryGroups, menuShowTitle, menuShowNames, menuShowItemImages,
    categories,
    qrIncludeLogo, qrLogoSize, qrLogoPlacement, qrIncludeUrl, qrCustomText, qrCustomTextBold, qrCustomTextItalic,
    saveSettings,
  ]);

  // ── SNAPSHOT ──────────────────────────────────────────────────────────────
  const handleTakeSnapshot = async () => {
    try {
      setSnapshotStatus(t('saving') || 'Saving...');
      const res = await fetch(`${BACKEND_URL}/api/public/menu/snapshot`, { method: 'POST' }); // eslint-disable-line
      if (res.ok) {
        const data = await res.json();
        setSnapshotDate(data.snapshotDate);
        setSnapshotStatus(t('snapshotSaved'));
        setTimeout(() => setSnapshotStatus(''), 3000);
      } else {
        setSnapshotStatus(t('error') || 'Error');
        setTimeout(() => setSnapshotStatus(''), 3000);
      }
    } catch (err) {
      setSnapshotStatus(t('error') || 'Error');
      setTimeout(() => setSnapshotStatus(''), 3000);
    }
  };

  // ── LOGO ──────────────────────────────────────────────────────────────────
  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('Logo must be under 2MB'); return; }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setLogoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', logoFile);
      const res = await fetch(`${BACKEND_URL}/api/branding/logo`, { method: 'POST', body: formData });
      if (res.ok) {
        setCurrentLogoUrl(`${BACKEND_URL}/api/branding/logo?t=${Date.now()}`);
        setLogoFile(null);
        setLogoPreview(null);
        if (onBrandingChange) onBrandingChange();
      }
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    await fetch(`${BACKEND_URL}/api/branding/logo`, { method: 'DELETE' });
    setCurrentLogoUrl('');
    if (onBrandingChange) onBrandingChange();
  };

  // ── CATEGORY ORDER ────────────────────────────────────────────────────────
  const moveCategoryUp = (idx, subset) => {
    if (idx === 0) return;
    if (subset) {
      // Reorder within a subset (group), then map back to main array
      const swapped = [...subset];
      [swapped[idx - 1], swapped[idx]] = [swapped[idx], swapped[idx - 1]];
      const orderMap = {};
      swapped.forEach((c, i) => { orderMap[c.id] = i; });
      setCategories(prev => {
        const next = prev.map(c => orderMap[c.id] !== undefined ? { ...c, menuOrdinal: orderMap[c.id] } : c);
        return next;
      });
    } else {
      const next = [...categories];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      next.forEach((c, i) => { c.menuOrdinal = i; });
      setCategories(next);
    }
  };

  const moveCategoryDown = (idx, subset) => {
    if (subset) {
      if (idx === subset.length - 1) return;
      const swapped = [...subset];
      [swapped[idx], swapped[idx + 1]] = [swapped[idx + 1], swapped[idx]];
      const orderMap = {};
      swapped.forEach((c, i) => { orderMap[c.id] = i; });
      setCategories(prev => {
        const next = prev.map(c => orderMap[c.id] !== undefined ? { ...c, menuOrdinal: orderMap[c.id] } : c);
        return next;
      });
    } else {
      if (idx === categories.length - 1) return;
      const next = [...categories];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      next.forEach((c, i) => { c.menuOrdinal = i; });
      setCategories(next);
    }
  };

  const toggleCategoryVisible = (catId) => {
    setCategories(prev => prev.map(c => c.id === catId ? { ...c, visible: !c.visible } : c));
  };

  // ── GROUP CRUD ──────────────────────────────────────────────────────────
  const addGroup = () => {
    const g = { id: `g_${Date.now()}`, name: t('newGroup'), name_es: '', ordinal: menuCategoryGroups.length };
    setMenuCategoryGroups(prev => [...prev, g]);
  };

  const renameGroup = (id, field, value) => {
    setMenuCategoryGroups(prev => prev.map(g => g.id === id ? { ...g, [field]: value } : g));
  };

  const deleteGroup = (id) => {
    setMenuCategoryGroups(prev => prev.filter(g => g.id !== id));
    setCategories(prev => prev.map(c => c.groupId === id ? { ...c, groupId: null } : c));
  };

  const moveGroupUp = (idx) => {
    if (idx === 0) return;
    const next = [...menuCategoryGroups];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    next.forEach((g, i) => { g.ordinal = i; });
    setMenuCategoryGroups(next);
  };

  const moveGroupDown = (idx) => {
    if (idx === menuCategoryGroups.length - 1) return;
    const next = [...menuCategoryGroups];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    next.forEach((g, i) => { g.ordinal = i; });
    setMenuCategoryGroups(next);
  };

  const assignCategoryToGroup = (catId, groupId) => {
    setCategories(prev => prev.map(c => c.id === catId ? { ...c, groupId: groupId || null } : c));
  };

  // Drop a category at a specific position within a group (or ungrouped)
  const dropCategoryAtPosition = (draggedCatId, targetGroupId, targetCatId, half) => {
    setCategories(prev => {
      const next = prev.map(c => ({ ...c }));
      const draggedIdx = next.findIndex(c => c.id === draggedCatId);
      if (draggedIdx === -1) return prev;

      // Assign group
      next[draggedIdx].groupId = targetGroupId || null;

      // Get sorted subset for this group to figure out ordinals
      const groupIds = new Set(menuCategoryGroups.map(g => g.id));
      const subset = next
        .filter(c => targetGroupId ? c.groupId === targetGroupId : (!c.groupId || !groupIds.has(c.groupId)))
        .sort((a, b) => (a.menuOrdinal ?? 9999) - (b.menuOrdinal ?? 9999));

      if (targetCatId) {
        // Remove dragged from subset if present, then insert at position
        const without = subset.filter(c => c.id !== draggedCatId);
        const targetIdx = without.findIndex(c => c.id === targetCatId);
        const insertIdx = half === 'bottom' ? targetIdx + 1 : targetIdx;
        without.splice(insertIdx, 0, next[draggedIdx]);
        // Reassign ordinals
        without.forEach((c, i) => {
          const mainIdx = next.findIndex(n => n.id === c.id);
          if (mainIdx !== -1) next[mainIdx].menuOrdinal = i;
        });
      } else {
        // Dropped on empty group area — put at end
        const without = subset.filter(c => c.id !== draggedCatId);
        without.push(next[draggedIdx]);
        without.forEach((c, i) => {
          const mainIdx = next.findIndex(n => n.id === c.id);
          if (mainIdx !== -1) next[mainIdx].menuOrdinal = i;
        });
      }

      return next;
    });
  };

  const toggleItemVisible = (catId, itemId) => {
    setCategories(prev => prev.map(c => {
      if (c.id !== catId) return c;
      return { ...c, items: c.items.map(item => item.id === itemId ? { ...item, visible: !item.visible } : item) };
    }));
  };

  const toggleCategoryExpanded = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  // ── QR PDF DOWNLOAD ───────────────────────────────────────────────────────
  const handleDownloadQrPdf = async () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;

    const isHorizontal = qrLogoPlacement === 'left' || qrLogoPlacement === 'right';
    const qrSizeMM = isHorizontal ? 44 : 52;
    const marginSide = 4;
    const marginTop = isHorizontal ? 14 : 8;
    const marginBottom = 8;
    const gap = 4;

    // Only use the target width from LOGO_DIMS; height is derived from actual aspect ratio below.
    const [lwFull] = LOGO_DIMS[qrLogoSize]?.mm || LOGO_DIMS.medium.mm;
    const lw = Math.min(lwFull, 72); // sanity cap — never wider than large preset

    // Horizontal page expands to fit logo + QR at the selected size.
    // Vertical page is fixed 80mm wide.
    const pageW = (isHorizontal && qrIncludeLogo && currentLogoUrl)
      ? marginSide + lw + gap + qrSizeMM + marginSide
      : 80;

    const qrDataUrl = canvas.toDataURL('image/png');

    // Fetch logo + measure natural dimensions for exact aspect ratio
    let logoDataUrl = null;
    let lh = lw * 0.5; // fallback until measured
    if (qrIncludeLogo && currentLogoUrl) {
      try {
        const logoRes = await fetch(`${BACKEND_URL}/api/branding/logo`);
        if (logoRes.ok) {
          const blob = await logoRes.blob();
          logoDataUrl = await new Promise(resolve => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          lh = await new Promise(resolve => {
            const img = new window.Image();
            img.onload = () => resolve(lw * (img.naturalHeight / img.naturalWidth));
            img.onerror = () => resolve(lw * 0.5);
            img.src = logoDataUrl;
          });
        }
      } catch (e) { /* no logo */ }
    }

    // Pre-measure text lines using a temporary PDF instance so we can size the page correctly
    const customFontStyle = qrCustomTextBold && qrCustomTextItalic ? 'bolditalic' : qrCustomTextBold ? 'bold' : qrCustomTextItalic ? 'italic' : 'normal';
    const measurePdf = new jsPDF({ unit: 'mm' });
    measurePdf.setFont('helvetica', customFontStyle);
    measurePdf.setFontSize(8);
    const customLines = qrCustomText
      ? qrCustomText.split('\n').flatMap(line => measurePdf.splitTextToSize(line, pageW - 8))
      : [];
    const customLineHeightMM = 4.5; // ~8pt in mm
    const urlHeightMM = qrIncludeUrl ? 5 : 0;
    const customHeightMM = customLines.length > 0 ? customLines.length * customLineHeightMM : 0;

    // Calculate total page height based on layout
    let pageH;
    if (isHorizontal) {
      const contentH = Math.max(qrSizeMM, logoDataUrl ? lh : 0);
      pageH = marginTop + contentH + (urlHeightMM ? 5 + urlHeightMM : 0) + customHeightMM + marginBottom;
    } else {
      let h = marginTop;
      if (qrLogoPlacement === 'above' && logoDataUrl) h += lh + gap;
      h += qrSizeMM + gap;
      if (qrLogoPlacement === 'below' && logoDataUrl) h += lh + 3;
      h += urlHeightMM + customHeightMM + marginBottom;
      pageH = h;
    }

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [pageW, pageH] });

    if (isHorizontal) {
      const totalW = (logoDataUrl ? lw + gap : 0) + qrSizeMM;
      const startX = (pageW - totalW) / 2;
      const startY = marginTop;

      let qrX, logoX;
      if (qrLogoPlacement === 'left') {
        logoX = startX;
        qrX = startX + (logoDataUrl ? lw + gap : 0);
      } else {
        qrX = startX;
        logoX = startX + qrSizeMM + gap;
      }

      if (logoDataUrl) {
        const logoY = startY + (qrSizeMM - lh) / 2;
        pdf.addImage(logoDataUrl, 'PNG', logoX, logoY, lw, lh);
      }
      pdf.addImage(qrDataUrl, 'PNG', qrX, startY, qrSizeMM, qrSizeMM);

      let textY = startY + qrSizeMM + 5;
      if (qrIncludeUrl) {
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(6); pdf.setTextColor(120);
        pdf.text(menuUrl, pageW / 2, textY, { align: 'center' });
        textY += urlHeightMM;
      }
      if (customLines.length > 0) {
        pdf.setFont('helvetica', customFontStyle); pdf.setFontSize(8); pdf.setTextColor(60);
        pdf.text(customLines, pageW / 2, textY, { align: 'center' });
      }
    } else {
      let y = marginTop;

      if (qrLogoPlacement === 'above' && logoDataUrl) {
        pdf.addImage(logoDataUrl, 'PNG', (pageW - lw) / 2, y, lw, lh);
        y += lh + gap;
      }

      pdf.addImage(qrDataUrl, 'PNG', (pageW - qrSizeMM) / 2, y, qrSizeMM, qrSizeMM);
      y += qrSizeMM + gap;

      if (qrLogoPlacement === 'below' && logoDataUrl) {
        pdf.addImage(logoDataUrl, 'PNG', (pageW - lw) / 2, y, lw, lh);
        y += lh + 3;
      }

      if (qrIncludeUrl) {
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(6); pdf.setTextColor(120);
        pdf.text(menuUrl, pageW / 2, y, { align: 'center' });
        y += urlHeightMM;
      }
      if (customLines.length > 0) {
        pdf.setFont('helvetica', customFontStyle); pdf.setFontSize(8); pdf.setTextColor(60);
        pdf.text(customLines, pageW / 2, y, { align: 'center' });
      }
    }

    pdf.save('menu-qr.pdf');
  };

  // ── SHARED MENU RENDERING ─────────────────────────────────────────────────
  // Used by both the inline preview and the PDF export (via html2canvas).
  const buildMenuElement = (lang) => {
    const bgColor = menuBgColor || '#f9f9f9';
    const textColor = menuTextColor || '#1a1a1a';
    const headerColor = menuHeaderColor || '#333333';
    const accentColor = menuAccentColor || '#1976d2';
    const accentHex = Math.round((menuAccentOpacity ?? 7) * 2.55).toString(16).padStart(2, '0');
    const cols = parseInt(menuColumns) || 1;
    const fontFamily = menuFont || 'system-ui, -apple-system, sans-serif';
    const logoWidthMap = { small: 80, medium: 120, large: 180 };
    const logoW = menuLogoSize === 'custom'
      ? (parseInt(menuLogoCustomWidth) || 120)
      : (logoWidthMap[menuLogoSize] || 120);

    const visibleCats = categories
      .filter(c => c.visible !== false)
      .map(c => ({ ...c, items: c.items.filter(i => i.visible !== false) }))
      .filter(c => c.items.length > 0);

    const formatPrice = (price) =>
      new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(price);

    const showCatNames = menuShowNames === 'both' || menuShowNames === 'categories';
    const showGroupNames = menuShowNames === 'both' || menuShowNames === 'groups';

    const renderCard = (cat, extraStyle = {}) => (
      <div key={cat.id} data-pdf-break="card" style={{
        backgroundColor: bgColor, borderRadius: 8, overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)', ...extraStyle,
      }}>
        {showCatNames && (
          <div style={{ padding: '0.875rem 1.1rem', borderBottom: `2px solid ${accentColor}`,
            backgroundColor: `${accentColor}${accentHex}` }}>
            <span style={{ fontSize: '1rem', fontWeight: 700, color: headerColor }}>{isSpanish ? (cat.name_es || cat.name) : cat.name}</span>
          </div>
        )}
        <div>
          {cat.items.map((item, i) => (
            <div key={item.id} data-pdf-break="item" style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              padding: '0.75rem 1.1rem', gap: '0.75rem',
              borderBottom: i < cat.items.length - 1 ? `1px solid ${textColor}11` : 'none',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: '0.9rem', fontWeight: 500, color: textColor }}>{item.name}</span>
                {(lang === 'es' ? (item.description_es || item.description) : item.description) && (
                  <span style={{ fontSize: '0.78rem', color: `${textColor}77`, lineHeight: 1.4 }}>
                    {lang === 'es' ? (item.description_es || item.description) : item.description}
                  </span>
                )}
              </div>
              <span style={{ fontSize: '0.9rem', fontWeight: 700, color: accentColor,
                whiteSpace: 'nowrap', flexShrink: 0 }}>{formatPrice(item.price)}</span>
            </div>
          ))}
        </div>
      </div>
    );

    const renderCatsLayout = (cats) => {
      const threshold = parseInt(menuLayoutThreshold) || 5;
      if (cats.length === 0) return null;
      if (cols === 1) {
        return cats.map(cat => (
          <div key={cat.id} style={{ marginBottom: '0.75rem' }}>
            {renderCard(cat)}
          </div>
        ));
      } else if (menuLayout === 'grid') {
        return (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '1rem' }}>
            {cats.map(cat => renderCard(cat))}
          </div>
        );
      } else if (menuLayout === 'flow') {
        return (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '1rem' }}>
            {cats.map(cat => {
              const spans = cat.items.length >= threshold;
              return renderCard(cat, spans ? { gridColumn: `span ${cols}` } : {});
            })}
          </div>
        );
      } else if (menuLayout === 'compact') {
        const colBuckets = Array.from({ length: cols }, () => []);
        const colCounts = Array(cols).fill(0);
        const sorted = [...cats].sort((a, b) => b.items.length - a.items.length);
        sorted.forEach(cat => {
          const minCol = colCounts.indexOf(Math.min(...colCounts));
          colBuckets[minCol].push(cat);
          colCounts[minCol] += cat.items.length;
        });
        return (
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            {colBuckets.map((bucket, ci) => (
              <div key={ci} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {bucket.map(cat => renderCard(cat))}
              </div>
            ))}
          </div>
        );
      } else {
        // masonry
        return (
          <div style={{ columns: cols, columnGap: '1rem', columnFill: 'balance' }}>
            {cats.map(cat => {
              const avoidBreak = cat.items.length < threshold;
              return renderCard(cat, {
                breakInside: avoidBreak ? 'avoid-column' : 'auto',
                display: 'inline-block', width: '100%', marginBottom: '1rem',
              });
            })}
          </div>
        );
      }
    };

    let menuContent;
    if (visibleCats.length === 0) {
      menuContent = (
        <p style={{ textAlign: 'center', color: `${textColor}88`, padding: '1rem', fontSize: '0.72rem' }}>
          No visible categories
        </p>
      );
    } else if (menuCategoryGroups.length > 0) {
      const groupIds = new Set(menuCategoryGroups.map(g => g.id));
      const ungroupedCats = visibleCats.filter(c => !c.groupId || !groupIds.has(c.groupId));
      menuContent = (
        <>
          {menuCategoryGroups.map(group => {
            const groupCats = visibleCats.filter(c => c.groupId === group.id);
            if (groupCats.length === 0) return null;
            return (
              <div key={group.id} data-pdf-break="group" style={{ marginBottom: '2rem' }}>
                {showGroupNames && (
                  <h2 data-pdf-break="group-heading" style={{ fontSize: '1.2rem', fontWeight: 700, color: headerColor, margin: '0 0 0.75rem',
                    borderBottom: `2px solid ${accentColor}`, paddingBottom: '0.4rem' }}>
                    {isSpanish ? (group.name_es || group.name) : group.name}
                  </h2>
                )}
                {renderCatsLayout(groupCats)}
              </div>
            );
          })}
          {ungroupedCats.length > 0 && renderCatsLayout(ungroupedCats)}
        </>
      );
    } else {
      menuContent = renderCatsLayout(visibleCats);
    }

    const innerW = cols > 1 ? 1200 : 720;

    return (
      <div style={{ width: innerW, backgroundColor: bgColor, fontFamily, color: textColor }}>
        {/* Header */}
        <div style={{ padding: '1.5rem 1rem 1rem', textAlign: 'center',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
          {currentLogoUrl && (
            <img src={currentLogoUrl} alt="Logo" crossOrigin="anonymous"
              style={{ width: logoW, height: 'auto', marginBottom: '0.25rem' }}
              onError={(e) => { e.target.style.display = 'none'; }} />
          )}
          {menuShowTitle && (
            <p style={{ margin: 0, fontSize: '0.9rem', color: headerColor, fontWeight: 500,
              textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              {lang === 'es' ? 'Nuestro Menú' : 'Our Menu'}
            </p>
          )}
        </div>
        {/* Content */}
        <div style={{ maxWidth: innerW, width: '100%', margin: '0 auto', padding: '1rem', boxSizing: 'border-box' }}>
          {menuContent}
        </div>
        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '1.5rem', borderTop: `1px solid ${textColor}11` }}>
          <small style={{ color: `${textColor}44`, fontSize: '0.72rem' }}>© 2026 BOCA POS</small>
        </div>
      </div>
    );
  };

  // ── MENU PDF EXPORT (html2canvas → jsPDF) ──────────────────────────────────
  const handleExportMenuPdf = async (exportLang = 'en') => {
    setExportingPdf(exportLang);
    try {
      // 1. Create hidden container — width matches the menu's innerW
      const cols = parseInt(menuColumns) || 1;
      const containerW = cols > 1 ? 1200 : 720;
      const container = document.createElement('div');
      container.style.cssText = `position:fixed;left:-9999px;top:0;width:${containerW}px;z-index:-1;`;
      document.body.appendChild(container);

      // 2. Render the menu into it using React
      const root = createRoot(container);
      root.render(buildMenuElement(exportLang));

      // 3. Wait for fonts + images to load
      await document.fonts.ready;
      await new Promise(r => setTimeout(r, 300));

      // Wait for logo image if present
      const logoImg = container.querySelector('img[alt="Logo"]');
      if (logoImg && !logoImg.complete) {
        await new Promise(r => { logoImg.onload = r; logoImg.onerror = r; });
      }

      // 4. Capture with html2canvas
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        backgroundColor: menuBgColor || '#f9f9f9',
        width: containerW,
        windowWidth: containerW,
      });

      // 5. Single-page PDF sized to fit all content
      const pageW = 210; // A4 width in mm
      const pxPerMm = canvas.width / pageW;
      const pageH = canvas.height / pxPerMm;
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [pageW, pageH] });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      pdf.addImage(imgData, 'JPEG', 0, 0, pageW, pageH);

      pdf.save(`menu-${exportLang}.pdf`);

      // 6. Cleanup
      root.unmount();
      document.body.removeChild(container);
    } finally {
      setExportingPdf(null);
    }
  };

  if (loading) {
    return <div style={{ padding: '2rem', color: theme?.colors?.textSecondary || '#666' }}>{t('loading')}...</div>;
  }

  // ── STYLES ────────────────────────────────────────────────────────────────
  const labelStyle = {
    display: 'block',
    marginBottom: '0.4rem',
    fontWeight: '600',
    fontSize: '0.9rem',
    color: theme?.colors?.text || '#000',
  };

  const inputStyle = {
    padding: '0.5rem 0.75rem',
    backgroundColor: theme?.colors?.background || '#f5f5f5',
    border: `1px solid ${theme?.colors?.border || '#ccc'}`,
    borderRadius: theme?.borderRadius || 4,
    color: theme?.colors?.text || '#000',
    fontSize: '0.9rem',
    width: '100%',
  };

  const btnPrimary = {
    padding: '0.6rem 1.25rem',
    backgroundColor: theme?.colors?.primary || '#1976d2',
    color: theme?.colors?.textLight || '#fff',
    border: 'none',
    borderRadius: theme?.borderRadius || 4,
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '0.9rem',
  };

  const btnSecondary = {
    padding: '0.5rem 1rem',
    backgroundColor: theme?.colors?.secondary || '#555',
    color: theme?.colors?.textLight || '#fff',
    border: 'none',
    borderRadius: theme?.borderRadius || 4,
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '0.85rem',
  };


  // ── QR LIVE PREVIEW ───────────────────────────────────────────────────────
  // Preview uses last-saved QR settings (savedQrForPreview), not live state.
  // This prevents the preview from re-rendering on every keystroke.
  const pqr = savedQrForPreview || {
    qrIncludeLogo, qrLogoSize, qrLogoPlacement, qrIncludeUrl, qrCustomText, qrCustomTextBold, qrCustomTextItalic,
  };

  // Scale: PDF page is 80mm wide → 200px preview = 2.5px/mm
  const SCALE = 2.5;
  const isHorizontal = pqr.qrLogoPlacement === 'left' || pqr.qrLogoPlacement === 'right';
  const qrPreviewPx = isHorizontal ? 44 * SCALE : 52 * SCALE; // 110px or 130px
  const hasPreviewLogo = pqr.qrIncludeLogo && currentLogoUrl;
  const [lwPx, lhPx] = LOGO_DIMS[pqr.qrLogoSize]?.px || LOGO_DIMS.medium.px;
  const effLwPx = lwPx; // no cap — preview matches PDF sizing
  const effLhPx = isHorizontal ? lhPx * (effLwPx / lwPx) : lhPx;

  const previewLogoEl = hasPreviewLogo ? (
    <img
      src={currentLogoUrl}
      alt="logo"
      style={{ width: effLwPx, height: effLhPx, objectFit: 'contain', display: 'block' }}
    />
  ) : null;

  const previewQrEl = (
    <QRCodeCanvas value={menuUrl} size={Math.round(qrPreviewPx)} level="M" includeMargin={false} />
  );

  const previewUrlEl = pqr.qrIncludeUrl ? (
    <div style={{ fontSize: 7, color: '#888', textAlign: 'center', maxWidth: 180, wordBreak: 'break-all', marginTop: 3 }}>
      {menuUrl}
    </div>
  ) : null;

  const previewCustomEl = pqr.qrCustomText ? (
    <div style={{
      fontSize: 9,
      color: '#333',
      textAlign: 'center',
      maxWidth: 180,
      marginTop: 2,
      whiteSpace: 'pre-line',
      fontWeight: pqr.qrCustomTextBold ? 'bold' : 'normal',
      fontStyle: pqr.qrCustomTextItalic ? 'italic' : 'normal',
    }}>
      {pqr.qrCustomText}
    </div>
  ) : null;

  let previewContent;
  if (isHorizontal) {
    const gap = 10;
    const logoSideEl = previewLogoEl ? (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: effLwPx + gap }}>
        {previewLogoEl}
      </div>
    ) : null;
    const qrAndTextEl = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {previewQrEl}
        {previewUrlEl}
        {previewCustomEl}
      </div>
    );
    previewContent = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
        {pqr.qrLogoPlacement === 'left' ? <>{logoSideEl}{qrAndTextEl}</> : <>{qrAndTextEl}{logoSideEl}</>}
      </div>
    );
  } else {
    previewContent = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        {pqr.qrLogoPlacement === 'above' && previewLogoEl}
        {previewQrEl}
        {pqr.qrLogoPlacement === 'below' && previewLogoEl}
        {previewUrlEl}
        {previewCustomEl}
      </div>
    );
  }

  return (
    <div style={{ padding: '0', background: theme?.colors?.background || '#f5f5f5' }}>

      {/* Hidden high-res QR canvas for PDF extraction */}
      <div ref={qrRef} style={{ position: 'absolute', left: -9999, top: -9999, opacity: 0, pointerEvents: 'none' }}>
        <QRCodeCanvas value={menuUrl} size={200} level="M" includeMargin={false} />
      </div>

      {/* ── ENABLE + MODE ─────────────────────────────────────── */}
      <CollapsibleSection theme={theme}
        title={`⚙️ ${t('menuSettings')}`}
        isOpen={showMenuSettings}
        onToggle={() => setShowMenuSettings(!showMenuSettings)}
      >
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: theme?.colors?.text || '#000' }}>
            <input type="checkbox" checked={!!menuEnabled} onChange={e => setMenuEnabled(e.target.checked)}
              style={{ width: 18, height: 18, cursor: 'pointer' }} />
            <span style={{ fontWeight: 'bold' }}>{t('menuEnabled')}</span>
          </label>
          <p style={{ margin: '0.4rem 0 0 1.6rem', fontSize: '0.83rem', color: theme?.colors?.textSecondary || '#666' }}>
            {t('menuEnabledInfo')}
          </p>
        </div>

        <div style={{ opacity: menuEnabled ? 1 : 0.5, pointerEvents: menuEnabled ? 'auto' : 'none' }}>
          <label style={labelStyle}>{t('menuDisplayMode')}</label>
          <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem' }}>
            {['live', 'static'].map(mode => (
              <label key={mode} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', color: theme?.colors?.text || '#000' }}>
                <input type="radio" name="menuMode" value={mode} checked={menuMode === mode}
                  onChange={() => setMenuMode(mode)} style={{ cursor: 'pointer' }} />
                {mode === 'live' ? t('liveMode') : t('staticMode')}
              </label>
            ))}
          </div>

          {menuMode === 'static' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <button style={btnSecondary} onClick={handleTakeSnapshot}>{t('takeSnapshot')}</button>
              {snapshotStatus && <span style={{ color: theme?.colors?.success || '#4caf50', fontWeight: 'bold', fontSize: '0.9rem' }}>{snapshotStatus}</span>}
              {snapshotDate && !snapshotStatus && (
                <span style={{ fontSize: '0.8rem', color: theme?.colors?.textSecondary || '#888' }}>
                  Last snapshot: {new Date(snapshotDate).toLocaleString()}
                </span>
              )}
            </div>
          )}

          {/* Show menu title toggle */}
          <div style={{ marginTop: '1.25rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: theme?.colors?.text || '#000' }}>
              <input type="checkbox" checked={!!menuShowTitle} onChange={e => setMenuShowTitle(e.target.checked)}
                style={{ width: 18, height: 18, cursor: 'pointer' }} />
              <span style={{ fontWeight: 'bold' }}>{t('showMenuTitle')}</span>
            </label>
          </div>

          {/* Name display options — only visible when groups exist */}
          {menuCategoryGroups.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <label style={labelStyle}>{t('nameDisplay')}</label>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {[
                  { value: 'both', label: t('nameDisplayBoth') },
                  { value: 'groups', label: t('nameDisplayGroups') },
                  { value: 'categories', label: t('nameDisplayCategories') },
                  { value: 'none', label: t('nameDisplayNone') },
                ].map(opt => (
                  <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', color: theme?.colors?.text || '#000', fontSize: '0.88rem' }}>
                    <input type="radio" name="menuShowNames" value={opt.value} checked={menuShowNames === opt.value}
                      onChange={() => setMenuShowNames(opt.value)} style={{ cursor: 'pointer' }} />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* ── LOGO ──────────────────────────────────────────────── */}
      <CollapsibleSection theme={theme}
        title="🖼️ Logo"
        isOpen={showLogo}
        onToggle={() => setShowLogo(!showLogo)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          {(logoPreview || currentLogoUrl) && (
            <img src={logoPreview || currentLogoUrl} alt="Logo"
              style={{ maxHeight: 60, maxWidth: 180, objectFit: 'contain', border: `1px solid ${theme?.colors?.border || '#ddd'}`, borderRadius: 4, padding: 4, background: '#fff' }} />
          )}
          {!currentLogoUrl && !logoPreview && (
            <span style={{ fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#888' }}>Using default Boca logo</span>
          )}
          <input type="file" accept=".svg,.png,.jpg,.jpeg" onChange={handleLogoFileChange}
            style={{ fontSize: '0.85rem', color: theme?.colors?.text || '#000' }} />
          {logoFile && (
            <button style={btnPrimary} onClick={handleLogoUpload} disabled={uploadingLogo}>
              {uploadingLogo ? '...' : t('uploadLogo') || 'Upload'}
            </button>
          )}
          {currentLogoUrl && (
            <button style={{ ...btnSecondary, backgroundColor: theme?.colors?.error || '#c62828' }} onClick={handleLogoDelete}>
              {t('resetLogo') || 'Remove Logo'}
            </button>
          )}
        </div>
        <p style={{ margin: '0.5rem 0 0', fontSize: '0.78rem', color: theme?.colors?.textSecondary || '#888' }}>
          Note: This logo is shared across the entire app and the public menu page.
        </p>

        {/* Menu page logo size */}
        <div style={{ marginTop: '1.25rem' }}>
          <label style={labelStyle}>Logo size on menu page</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            {[
              { value: 'small',  label: 'Small',  width: 80 },
              { value: 'medium', label: 'Medium', width: 120 },
              { value: 'large',  label: 'Large',  width: 180 },
              { value: 'custom', label: 'Custom', width: null },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setMenuLogoSize(opt.value)}
                style={{
                  padding: '0.3rem 0.7rem',
                  border: `2px solid ${menuLogoSize === opt.value ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.border || '#ccc')}`,
                  borderRadius: theme?.borderRadius || 4,
                  backgroundColor: menuLogoSize === opt.value ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.surface || '#fff'),
                  color: menuLogoSize === opt.value ? (theme?.colors?.textLight || '#fff') : (theme?.colors?.text || '#000'),
                  fontWeight: menuLogoSize === opt.value ? 'bold' : 'normal',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                }}
              >
                {opt.label}
                {opt.width && <span style={{ display: 'block', fontSize: '0.7rem', opacity: 0.75 }}>{opt.width}px</span>}
              </button>
            ))}
            {menuLogoSize === 'custom' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: theme?.colors?.text || '#000' }}>
                <input
                  type="number"
                  min="16"
                  max="1200"
                  value={getNumberInputValue(menuLogoCustomWidth)}
                  onChange={e => setMenuLogoCustomWidth(parseNumberInput(e.target.value, { min: 16, max: 1200 }))}
                  onWheel={e => e.target.blur()}
                  placeholder="80"
                  style={{
                    width: 72, padding: '0.3rem 0.5rem',
                    border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                    borderRadius: theme?.borderRadius || 4,
                    fontSize: '0.85rem',
                    backgroundColor: theme?.colors?.surface || '#fff',
                    color: theme?.colors?.text || '#000',
                  }}
                />
                <span style={{ color: theme?.colors?.textSecondary || '#666' }}>px width <span style={{ opacity: 0.65 }}>(max 1200)</span></span>
              </label>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {/* ── COLORS & FONT ─────────────────────────────────────── */}
      <CollapsibleSection theme={theme}
        title={`🎨 ${t('menuColors')} & ${t('menuFont')}`}
        isOpen={showColorsFont}
        onToggle={() => setShowColorsFont(!showColorsFont)}
      >
        {/* Font */}
        <div style={{ marginBottom: '1.25rem' }}>
          <label style={labelStyle}>{t('menuFont')}</label>
          <select value={menuFont} onChange={e => setMenuFont(e.target.value)}
            style={{ ...inputStyle, maxWidth: 280, fontFamily: menuFont || 'inherit' }}>
            {FONT_OPTIONS.map(f => (
              <option key={f.value} value={f.value} style={{ fontFamily: f.value || 'inherit' }}>{f.label}</option>
            ))}
          </select>
          {menuFont && (
            <p style={{ margin: '0.4rem 0 0', fontSize: '0.9rem', color: theme?.colors?.textSecondary || '#666', fontFamily: menuFont }}>
              Preview: The quick brown fox jumps over the lazy dog
            </p>
          )}
        </div>

        {/* Colors */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
          {[
            { label: t('menuBgColor'), value: menuBgColor, setter: setMenuBgColor },
            { label: t('menuTextColor'), value: menuTextColor, setter: setMenuTextColor },
            { label: t('menuHeaderColor'), value: menuHeaderColor, setter: setMenuHeaderColor },
            { label: t('menuAccentColor'), value: menuAccentColor, setter: setMenuAccentColor },
          ].map(({ label, value, setter }) => (
            <div key={label}>
              <label style={labelStyle}>{label}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input type="color" value={value || '#ffffff'} onChange={e => setter(e.target.value)}
                  style={{ width: 44, height: 36, padding: 2, border: `1px solid ${theme?.colors?.border || '#ccc'}`, borderRadius: 4, cursor: 'pointer', backgroundColor: 'transparent' }} />
                <input type="text" value={value} onChange={e => setter(e.target.value)}
                  style={{ ...inputStyle, width: 100, fontFamily: 'monospace', fontSize: '0.85rem' }} />
              </div>
            </div>
          ))}
        </div>

        {/* Accent header opacity */}
        <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.88rem', fontWeight: '600', color: theme?.colors?.text || '#000', whiteSpace: 'nowrap' }}>
            Category header tint:
          </label>
          <input
            type="range"
            min="0" max="40"
            value={menuAccentOpacity}
            onChange={e => setMenuAccentOpacity(parseInt(e.target.value))}
            style={{ width: 120, cursor: 'pointer' }}
          />
          <span style={{ fontSize: '0.82rem', color: theme?.colors?.textSecondary || '#666', minWidth: 32 }}>
            {menuAccentOpacity}%
          </span>
          <div style={{
            width: 28, height: 28, borderRadius: 4,
            border: `1px solid ${theme?.colors?.border || '#ccc'}`,
            backgroundColor: `${menuAccentColor || '#1976d2'}${Math.round(menuAccentOpacity * 2.55).toString(16).padStart(2, '0')}`,
          }} />
        </div>

        {/* Live preview swatch */}
        <div style={{ marginTop: '1.25rem', padding: '1rem', borderRadius: 6, fontFamily: menuFont || 'inherit',
          backgroundColor: menuBgColor || '#f9f9f9', color: menuTextColor || '#1a1a1a', border: `1px solid ${theme?.colors?.border || '#ddd'}` }}>
          <div style={{ fontWeight: 'bold', color: menuHeaderColor || '#333', fontSize: '0.95rem', borderBottom: `2px solid ${menuAccentColor || '#1976d2'}`, paddingBottom: '0.3rem', marginBottom: '0.5rem',
            backgroundColor: `${menuAccentColor || '#1976d2'}${Math.round(menuAccentOpacity * 2.55).toString(16).padStart(2, '0')}`, padding: '0.3rem 0.5rem', borderRadius: 4 }}>
            Sample Category
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Menu Item Name</span>
            <span style={{ color: menuAccentColor || '#1976d2', fontWeight: 600 }}>$120.00</span>
          </div>
        </div>
      </CollapsibleSection>

      {/* ── COLUMNS ───────────────────────────────────────────── */}
      <CollapsibleSection theme={theme}
        title={`📐 ${t('menuColumns')}`}
        isOpen={showColumns}
        onToggle={() => setShowColumns(!showColumns)}
      >
        {/* Column count — per breakpoint */}
        {(() => {
          const colBtnStyle = (selected) => ({
            minWidth: 44, height: 36, padding: '0 0.6rem',
            border: `2px solid ${selected ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.border || '#ccc')}`,
            borderRadius: theme?.borderRadius || 4,
            backgroundColor: selected ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.surface || '#fff'),
            color: selected ? (theme?.colors?.textLight || '#fff') : (theme?.colors?.text || '#000'),
            fontWeight: selected ? 'bold' : 'normal',
            cursor: 'pointer',
            fontSize: '0.95rem',
          });
          const ColumnRow = ({ label, hint, value, setValue, includeAuto }) => (
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ ...labelStyle, marginBottom: '0.4rem' }}>{label}</label>
              {hint && <div style={{ fontSize: '0.75rem', color: theme?.colors?.textSecondary || '#666', marginBottom: '0.4rem' }}>{hint}</div>}
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {includeAuto && (
                  <button onClick={() => setValue('')} style={colBtnStyle(value === '')}>{t('auto')}</button>
                )}
                {COLUMN_OPTIONS.map(opt => (
                  <button key={opt.value} onClick={() => setValue(opt.value)} style={colBtnStyle(value === opt.value)}>{opt.label}</button>
                ))}
              </div>
            </div>
          );
          return (
            <div style={{ marginBottom: '1.5rem' }}>
              <ColumnRow
                label={`${t('desktopColumns')} (> 900px)`}
                value={menuColumns}
                setValue={setMenuColumns}
                includeAuto={false}
              />
              <ColumnRow
                label={`${t('mobileColumns')} (601–900px)`}
                hint={t('autoUsesDesktopFallback')}
                value={menuColumnsMobile}
                setValue={setMenuColumnsMobile}
                includeAuto={true}
              />
              <ColumnRow
                label={`${t('smallScreenColumns')} (≤ 600px)`}
                value={menuColumnsSmall}
                setValue={setMenuColumnsSmall}
                includeAuto={true}
              />
            </div>
          );
        })()}

        {/* Layout mode — disabled when 1 column */}
        <div style={{ opacity: menuColumns === '1' ? 0.4 : 1, pointerEvents: menuColumns === '1' ? 'none' : 'auto' }}>
          <label style={{ ...labelStyle, marginBottom: '0.6rem' }}>Layout mode</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
            {LAYOUT_OPTIONS.map(opt => {
              const selected = menuLayout === opt.value;
              return (
                <div
                  key={opt.value}
                  onClick={() => setMenuLayout(opt.value)}
                  style={{
                    padding: '0.75rem 1rem',
                    border: `2px solid ${selected ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.border || '#ccc')}`,
                    borderRadius: theme?.borderRadius || 6,
                    backgroundColor: selected ? `${theme?.colors?.primary || '#1976d2'}11` : (theme?.colors?.surface || '#fff'),
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                    <LayoutIcon type={opt.value}
                      color={selected ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.textSecondary || '#999')}
                      borderColor={theme?.colors?.border || '#ddd'} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '0.9rem', color: selected ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.text || '#000'), marginBottom: '0.25rem' }}>
                        {opt.label}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: theme?.colors?.textSecondary || '#666', lineHeight: 1.4 }}>
                        {opt.desc}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Threshold — only for Flow and Masonry */}
          {(menuLayout === 'flow' || menuLayout === 'masonry') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' }}>
              <label style={{ fontSize: '0.88rem', fontWeight: '600', color: theme?.colors?.text || '#000', whiteSpace: 'nowrap' }}>
                Min items to span columns:
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={getNumberInputValue(menuLayoutThreshold)}
                onChange={e => setMenuLayoutThreshold(parseNumberInput(e.target.value, { min: 1, max: 20 }))}
                onWheel={e => e.target.blur()}
                placeholder="5"
                style={{ width: 72, padding: '0.35rem 0.5rem', border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                  borderRadius: theme?.borderRadius || 4, fontSize: '0.9rem',
                  backgroundColor: theme?.colors?.surface || '#fff', color: theme?.colors?.text || '#000' }}
              />
              <span style={{ fontSize: '0.78rem', color: theme?.colors?.textSecondary || '#666' }}>
                Categories with at least this many items will span columns
              </span>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* ── PHOTOS ────────────────────────────────────────────── */}
      <CollapsibleSection theme={theme}
        title={`📷 ${t('photosSection')}`}
        isOpen={showPhotos}
        onToggle={() => setShowPhotos(!showPhotos)}
      >
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: theme?.colors?.text || '#000' }}>
            <input type="checkbox" checked={!!menuShowItemImages} onChange={e => setMenuShowItemImages(e.target.checked)}
              style={{ width: 18, height: 18, cursor: 'pointer' }} />
            <span style={{ fontWeight: 'bold' }}>{t('showItemImages')}</span>
          </label>
          <p style={{ margin: '0.4rem 0 0 1.6rem', fontSize: '0.83rem', color: theme?.colors?.textSecondary || '#666' }}>
            {t('showItemImagesHint')}
          </p>
        </div>
        <div style={{ marginTop: '0.75rem', padding: '0.75rem 1rem', borderRadius: theme?.borderRadius || 6,
          backgroundColor: `${theme?.colors?.primary || '#1976d2'}11`,
          border: `1px solid ${theme?.colors?.primary || '#1976d2'}33` }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: theme?.colors?.text || '#000', fontWeight: 600 }}>
            {t('photosHowToTitle')}
          </p>
          <p style={{ margin: '0.35rem 0 0', fontSize: '0.82rem', color: theme?.colors?.textSecondary || '#666', lineHeight: 1.5 }}>
            {t('photosHowToBody')}
          </p>
        </div>
      </CollapsibleSection>

      {/* ── LANGUAGE TOGGLE ───────────────────────────────────── */}
      <CollapsibleSection theme={theme}
        title="🌐 Language Toggle"
        isOpen={showLanguageToggle}
        onToggle={() => setShowLanguageToggle(!showLanguageToggle)}
      >
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: theme?.colors?.text || '#000' }}>
            <input type="checkbox" checked={!!menuLanguageToggle} onChange={e => setMenuLanguageToggle(e.target.checked)}
              style={{ width: 18, height: 18, cursor: 'pointer' }} />
            <span style={{ fontWeight: 'bold' }}>Show language switcher on public menu</span>
          </label>
          <p style={{ margin: '0.4rem 0 0 1.6rem', fontSize: '0.83rem', color: theme?.colors?.textSecondary || '#666' }}>
            Displays EN / ES buttons on the public menu page so visitors can switch the UI language.
          </p>
        </div>

        <div style={{ opacity: menuLanguageToggle ? 1 : 0.5, pointerEvents: menuLanguageToggle ? 'auto' : 'none' }}>
          <label style={labelStyle}>Default Language</label>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            {[{ value: 'en', label: 'English' }, { value: 'es', label: 'Español' }].map(lang => (
              <label key={lang.value} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', color: theme?.colors?.text || '#000' }}>
                <input type="radio" name="menuDefaultLanguage" value={lang.value} checked={menuDefaultLanguage === lang.value}
                  onChange={() => setMenuDefaultLanguage(lang.value)} style={{ cursor: 'pointer' }} />
                {lang.label}
              </label>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* ── CATEGORY VISIBILITY & ORDER ───────────────────────── */}
      <CollapsibleSection theme={theme}
        title={`📋 ${t('categoryVisibility')}`}
        isOpen={showCategoryVisibility}
        onToggle={() => setShowCategoryVisibility(!showCategoryVisibility)}
      >
        <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#666' }}>
          Toggle categories on/off and reorder them. Expand a category to control individual item visibility.
        </p>

        {loadError && (
          <p style={{ color: theme?.colors?.error || '#c62828', fontSize: '0.85rem', padding: '0.5rem', backgroundColor: '#ffebee', borderRadius: 4 }}>
            Error: {loadError}
          </p>
        )}
        {!loadError && categories.length === 0 && (
          <p style={{ color: theme?.colors?.textSecondary || '#888' }}>No categories found.</p>
        )}

        {/* Add Group button */}
        <div style={{ marginBottom: '1rem' }}>
          <button style={btnSecondary} onClick={addGroup}>+ {t('addGroup')}</button>
        </div>

        {/* Render groups with their categories */}
        {(() => {
          const groupIds = new Set(menuCategoryGroups.map(g => g.id));

          const renderCategoryRow = (cat, idx, subset) => {
            const isDropTarget = dragOverCatId === cat.id;
            const showTopLine = isDropTarget && dragOverHalf === 'top' && dragCatIdRef.current !== cat.id;
            const showBottomLine = isDropTarget && dragOverHalf === 'bottom' && dragCatIdRef.current !== cat.id;
            const lineStyle = { height: 2, backgroundColor: theme?.colors?.primary || '#1976d2', borderRadius: 1, margin: '0 0.5rem' };
            return (
            <div key={cat.id}>
              {showTopLine && <div style={lineStyle} />}
              <div
                draggable
                onDragStart={e => {
                  dragCatIdRef.current = cat.id;
                  e.dataTransfer.setData('text/plain', String(cat.id));
                  e.dataTransfer.effectAllowed = 'move';
                }}
                onDragEnd={() => { dragCatIdRef.current = null; setDragOverCatId(null); setDragOverHalf(null); setDragOverGroupId(null); }}
                onDragOver={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = 'move';
                  const rect = e.currentTarget.getBoundingClientRect();
                  const half = (e.clientY - rect.top) < rect.height / 2 ? 'top' : 'bottom';
                  setDragOverCatId(cat.id);
                  setDragOverHalf(half);
                  setDragOverGroupId(cat.groupId || '__ungrouped__');
                }}
                onDragLeave={e => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setDragOverCatId(null);
                    setDragOverHalf(null);
                  }
                }}
                onDrop={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  const draggedId = parseInt(e.dataTransfer.getData('text/plain'));
                  setDragOverCatId(null);
                  setDragOverHalf(null);
                  setDragOverGroupId(null);
                  if (draggedId && draggedId !== cat.id) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const half = (e.clientY - rect.top) < rect.height / 2 ? 'top' : 'bottom';
                    dropCategoryAtPosition(draggedId, cat.groupId || '', cat.id, half);
                  }
                }}
                style={{
                  marginBottom: '0.5rem',
                  border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                  borderRadius: theme?.borderRadius || 4,
                  overflow: 'hidden',
                  opacity: cat.visible ? 1 : 0.55,
                  cursor: 'grab',
                }}>
              <div style={{ display: 'flex', alignItems: 'center', padding: '0.6rem 0.75rem',
                backgroundColor: cat.visible ? (theme?.colors?.surface || '#fff') : (theme?.colors?.background || '#f5f5f5'),
                gap: '0.5rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <button onClick={() => moveCategoryUp(idx, subset)} disabled={idx === 0}
                    style={{ padding: '0 6px', fontSize: '0.7rem', lineHeight: 1.4, border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                      borderRadius: 3, cursor: idx === 0 ? 'default' : 'pointer', background: theme?.colors?.surface || '#fff',
                      color: idx === 0 ? (theme?.colors?.border || '#ccc') : (theme?.colors?.text || '#000') }}>▲</button>
                  <button onClick={() => moveCategoryDown(idx, subset)} disabled={idx === (subset || categories).length - 1}
                    style={{ padding: '0 6px', fontSize: '0.7rem', lineHeight: 1.4, border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                      borderRadius: 3, cursor: idx === (subset || categories).length - 1 ? 'default' : 'pointer', background: theme?.colors?.surface || '#fff',
                      color: idx === (subset || categories).length - 1 ? (theme?.colors?.border || '#ccc') : (theme?.colors?.text || '#000') }}>▼</button>
                </div>
                <input type="checkbox" checked={!!cat.visible} onChange={() => toggleCategoryVisible(cat.id)}
                  style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} />
                <span style={{ flex: 1, fontWeight: 600, fontSize: '0.9rem', color: theme?.colors?.text || '#000' }}>
                  {isSpanish ? (cat.name_es || cat.name) : cat.name}
                </span>
                {/* Group assignment dropdown */}
                {menuCategoryGroups.length > 0 && (
                  <select
                    value={cat.groupId || ''}
                    onChange={e => assignCategoryToGroup(cat.id, e.target.value)}
                    style={{ padding: '2px 4px', fontSize: '0.75rem', border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                      borderRadius: 3, background: theme?.colors?.surface || '#fff', color: theme?.colors?.text || '#000', maxWidth: 110 }}
                  >
                    <option value="">{t('ungrouped')}</option>
                    {menuCategoryGroups.map(g => (
                      <option key={g.id} value={g.id}>{isSpanish ? (g.name_es || g.name) : g.name}</option>
                    ))}
                  </select>
                )}
                <span style={{ fontSize: '0.78rem', color: theme?.colors?.textSecondary || '#888', marginRight: '0.5rem' }}>
                  {cat.items.filter(i => i.visible !== false).length}/{cat.items.length}
                </span>
                <button onClick={() => toggleCategoryExpanded(cat.id)}
                  style={{ padding: '2px 8px', fontSize: '0.75rem', border: `1px solid ${theme?.colors?.border || '#ddd'}`,
                    borderRadius: 3, cursor: 'pointer', background: theme?.colors?.surface || '#fff', color: theme?.colors?.text || '#000' }}>
                  {expandedCategories[cat.id] ? '▲' : '▼'}
                </button>
              </div>

              {expandedCategories[cat.id] && (
                <div style={{ borderTop: `1px solid ${theme?.colors?.border || '#eee'}` }}>
                  {cat.items.length === 0 && (
                    <p style={{ margin: '0.5rem 1rem', fontSize: '0.82rem', color: theme?.colors?.textSecondary || '#888' }}>No active items.</p>
                  )}
                  {cat.items.map(item => (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.45rem 1rem',
                      borderBottom: `1px solid ${theme?.colors?.background || '#f5f5f5'}`,
                      opacity: item.visible !== false ? 1 : 0.5,
                      backgroundColor: item.visible !== false ? 'transparent' : (theme?.colors?.background || '#f5f5f5') }}>
                      <input type="checkbox" checked={item.visible !== false} onChange={() => toggleItemVisible(cat.id, item.id)}
                        style={{ width: 14, height: 14, cursor: 'pointer', flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: '0.87rem', color: theme?.colors?.text || '#000' }}>{item.name}</span>
                      <ProductFeaturedControls product={item} onChanged={loadSettings} />
                      <span style={{ fontSize: '0.82rem', color: theme?.colors?.textSecondary || '#888' }}>
                        ${parseFloat(item.price).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {showBottomLine && <div style={lineStyle} />}
          </div>
          );
          };

          const sortedCats = [...categories]
            .filter(c => c.items.length > 0) // hide 0/0 categories (no active products)
            .sort((a, b) => (a.menuOrdinal ?? 9999) - (b.menuOrdinal ?? 9999));

          return (
            <>
              {menuCategoryGroups.map((group, gi) => {
                const groupCats = sortedCats.filter(c => c.groupId === group.id);
                const isGroupExpanded = expandedGroups[group.id] !== false;
                const isDragTarget = dragOverGroupId === group.id;
                const showBody = isGroupExpanded || isDragTarget;
                return (
                  <div key={group.id}
                    onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (!dragOverCatId) setDragOverGroupId(group.id); }}
                    onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) { setDragOverGroupId(null); setDragOverCatId(null); setDragOverHalf(null); } }}
                    onDrop={e => { e.preventDefault(); setDragOverGroupId(null); setDragOverCatId(null); setDragOverHalf(null); const catId = parseInt(e.dataTransfer.getData('text/plain')); if (catId) dropCategoryAtPosition(catId, group.id, null, null); }}
                    style={{
                      marginBottom: '1rem',
                      border: `2px solid ${isDragTarget ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.primary || '#1976d2') + '44'}`,
                      borderRadius: theme?.borderRadius || 6, overflow: 'hidden',
                      backgroundColor: isDragTarget ? `${theme?.colors?.primary || '#1976d2'}08` : 'transparent',
                      transition: 'border-color 0.15s, background-color 0.15s',
                    }}>
                    {/* Group header */}
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem',
                      backgroundColor: `${theme?.colors?.primary || '#1976d2'}11`,
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <button onClick={() => moveGroupUp(gi)} disabled={gi === 0}
                          style={{ padding: '0 6px', fontSize: '0.7rem', lineHeight: 1.4, border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                            borderRadius: 3, cursor: gi === 0 ? 'default' : 'pointer', background: theme?.colors?.surface || '#fff',
                            color: gi === 0 ? (theme?.colors?.border || '#ccc') : (theme?.colors?.text || '#000') }}>▲</button>
                        <button onClick={() => moveGroupDown(gi)} disabled={gi === menuCategoryGroups.length - 1}
                          style={{ padding: '0 6px', fontSize: '0.7rem', lineHeight: 1.4, border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                            borderRadius: 3, cursor: gi === menuCategoryGroups.length - 1 ? 'default' : 'pointer', background: theme?.colors?.surface || '#fff',
                            color: gi === menuCategoryGroups.length - 1 ? (theme?.colors?.border || '#ccc') : (theme?.colors?.text || '#000') }}>▼</button>
                      </div>
                      <input
                        type="text"
                        value={group.name}
                        onChange={e => renameGroup(group.id, 'name', e.target.value)}
                        placeholder="EN"
                        style={{
                          flex: 1, fontWeight: 700, fontSize: '0.95rem', padding: '0.25rem 0.5rem',
                          border: `1px solid ${theme?.colors?.border || '#ccc'}`, borderRadius: 3,
                          background: theme?.colors?.surface || '#fff', color: theme?.colors?.text || '#000',
                        }}
                      />
                      <input
                        type="text"
                        value={group.name_es || ''}
                        onChange={e => renameGroup(group.id, 'name_es', e.target.value)}
                        placeholder="ES"
                        style={{
                          flex: 1, fontWeight: 400, fontSize: '0.85rem', padding: '0.25rem 0.5rem',
                          border: `1px solid ${theme?.colors?.border || '#ccc'}`, borderRadius: 3,
                          background: theme?.colors?.surface || '#fff', color: theme?.colors?.textSecondary || '#666',
                          fontStyle: 'italic',
                        }}
                      />
                      <span style={{ fontSize: '0.78rem', color: theme?.colors?.textSecondary || '#888' }}>
                        {groupCats.length} cat{groupCats.length !== 1 ? 's' : ''}
                      </span>
                      <button onClick={() => deleteGroup(group.id)}
                        style={{ padding: '2px 8px', fontSize: '0.75rem', border: `1px solid ${theme?.colors?.error || '#c62828'}`,
                          borderRadius: 3, cursor: 'pointer', background: 'transparent', color: theme?.colors?.error || '#c62828' }}>
                        {t('deleteGroup')}
                      </button>
                      <button
                        onClick={() => setExpandedGroups(prev => ({ ...prev, [group.id]: !isGroupExpanded }))}
                        style={{ padding: '2px 6px', fontSize: '0.75rem', border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                          borderRadius: 3, cursor: 'pointer', background: theme?.colors?.surface || '#fff',
                          color: theme?.colors?.text || '#000', flexShrink: 0 }}>
                        {isGroupExpanded ? '▲' : '▼'}
                      </button>
                    </div>
                    {/* Categories in this group — collapsible, auto-expands on drag */}
                    {showBody && (
                      <div style={{ padding: '0.5rem 0.5rem 0', minHeight: groupCats.length === 0 ? 40 : 'auto' }}>
                        {groupCats.length === 0 && (
                          <p style={{ margin: '0 0 0.5rem 0.25rem', fontSize: '0.82rem', color: theme?.colors?.textSecondary || '#888', fontStyle: 'italic' }}>
                            Drop categories here or use the dropdown
                          </p>
                        )}
                        {groupCats.map((cat, idx) => renderCategoryRow(cat, idx, groupCats))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Ungrouped categories */}
              {(() => {
                const ungrouped = sortedCats.filter(c => !c.groupId || !groupIds.has(c.groupId));
                const hasGroups = menuCategoryGroups.length > 0;
                if (ungrouped.length === 0 && hasGroups) {
                  // Still render a drop target even when empty
                  return (
                    <div
                      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverGroupId('__ungrouped__'); }}
                      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverGroupId(null); }}
                      onDrop={e => { e.preventDefault(); setDragOverGroupId(null); const catId = parseInt(e.dataTransfer.getData('text/plain')); if (catId) assignCategoryToGroup(catId, ''); }}
                      style={{
                        marginTop: '0.5rem', padding: '0.75rem', minHeight: 40,
                        border: `2px dashed ${dragOverGroupId === '__ungrouped__' ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.border || '#ddd')}`,
                        borderRadius: theme?.borderRadius || 6,
                        backgroundColor: dragOverGroupId === '__ungrouped__' ? `${theme?.colors?.primary || '#1976d2'}08` : 'transparent',
                        transition: 'border-color 0.15s, background-color 0.15s',
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#888' }}>
                        {t('ungrouped')}
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    onDragOver={hasGroups ? (e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverGroupId('__ungrouped__'); }) : undefined}
                    onDragLeave={hasGroups ? (e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverGroupId(null); }) : undefined}
                    onDrop={hasGroups ? (e => { e.preventDefault(); setDragOverGroupId(null); const catId = parseInt(e.dataTransfer.getData('text/plain')); if (catId) assignCategoryToGroup(catId, ''); }) : undefined}
                    style={{
                      marginTop: hasGroups ? '0.5rem' : 0,
                      padding: hasGroups ? '0.5rem' : 0,
                      border: hasGroups ? `2px dashed ${dragOverGroupId === '__ungrouped__' ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.border || '#ddd')}` : 'none',
                      borderRadius: theme?.borderRadius || 6,
                      backgroundColor: dragOverGroupId === '__ungrouped__' ? `${theme?.colors?.primary || '#1976d2'}08` : 'transparent',
                      transition: 'border-color 0.15s, background-color 0.15s',
                    }}
                  >
                    {hasGroups && (
                      <div style={{ fontWeight: 600, fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#888', marginBottom: '0.5rem' }}>
                        {t('ungrouped')}
                      </div>
                    )}
                    {ungrouped.map((cat, idx) => renderCategoryRow(cat, idx, ungrouped))}
                  </div>
                );
              })()}
            </>
          );
        })()}
      </CollapsibleSection>

      {/* ── QR CODE ───────────────────────────────────────────── */}
      <CollapsibleSection theme={theme}
        title={`📱 ${t('qrCodeGenerator')}`}
        isOpen={showQrCode}
        onToggle={() => setShowQrCode(!showQrCode)}
      >
        {/* Settings + Preview side by side */}
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>

          {/* Left column: PDF settings */}
          <div style={{ flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            {/* Include URL */}
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: theme?.colors?.text || '#000' }}>
                <input type="checkbox" checked={qrIncludeUrl} onChange={e => setQrIncludeUrl(e.target.checked)}
                  style={{ width: 16, height: 16, cursor: 'pointer' }} />
                <span style={{ fontWeight: '600' }}>Include URL text</span>
              </label>
            </div>

            {/* Include logo */}
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: currentLogoUrl ? 'pointer' : 'default', color: theme?.colors?.text || '#000' }}>
                <input type="checkbox" checked={qrIncludeLogo} onChange={e => setQrIncludeLogo(e.target.checked)}
                  disabled={!currentLogoUrl}
                  style={{ width: 16, height: 16, cursor: currentLogoUrl ? 'pointer' : 'default' }} />
                <span style={{ fontWeight: '600' }}>Include logo on printout</span>
              </label>
              {!currentLogoUrl && (
                <p style={{ margin: '0.25rem 0 0 1.5rem', fontSize: '0.78rem', color: theme?.colors?.textSecondary || '#888' }}>
                  Upload a logo in the Logo section first
                </p>
              )}
            </div>

            {/* Logo size + placement */}
            <div style={{
              opacity: (qrIncludeLogo && currentLogoUrl) ? 1 : 0.4,
              pointerEvents: (qrIncludeLogo && currentLogoUrl) ? 'auto' : 'none',
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem',
            }}>
              <div>
                <label style={labelStyle}>Logo size</label>
                <select value={qrLogoSize} onChange={e => setQrLogoSize(e.target.value)} style={inputStyle}>
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Logo placement</label>
                <select value={qrLogoPlacement} onChange={e => setQrLogoPlacement(e.target.value)} style={inputStyle}>
                  <option value="above">Above QR</option>
                  <option value="below">Below QR</option>
                  <option value="left">Left of QR</option>
                  <option value="right">Right of QR</option>
                </select>
              </div>
            </div>

            {/* Custom text */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                <span style={{ fontWeight: '600', fontSize: '0.85rem', color: theme?.colors?.text || '#000' }}>Additional text:</span>
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  {[
                    { key: 'bold', label: 'B', active: qrCustomTextBold, set: setQrCustomTextBold, style: { fontWeight: 'bold' } },
                    { key: 'italic', label: 'I', active: qrCustomTextItalic, set: setQrCustomTextItalic, style: { fontStyle: 'italic' } },
                  ].map(({ key, label, active, set, style }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => set(v => !v)}
                      style={{
                        width: 28, height: 26,
                        border: `1px solid ${active ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.border || '#ccc')}`,
                        borderRadius: theme?.borderRadius || 4,
                        backgroundColor: active ? (theme?.colors?.primary || '#1976d2') : (theme?.colors?.surface || '#fff'),
                        color: active ? (theme?.colors?.textLight || '#fff') : (theme?.colors?.text || '#000'),
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        lineHeight: 1,
                        ...style,
                      }}
                    >{label}</button>
                  ))}
                </div>
              </div>
              <textarea
                value={qrCustomText}
                onChange={e => setQrCustomText(e.target.value)}
                placeholder={'e.g. "Scan for our menu"'}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.3rem 0.5rem',
                  border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                  borderRadius: theme?.borderRadius || 4,
                  fontSize: '0.85rem',
                  backgroundColor: theme?.colors?.surface || '#fff',
                  color: theme?.colors?.text || '#000',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  fontWeight: qrCustomTextBold ? 'bold' : 'normal',
                  fontStyle: qrCustomTextItalic ? 'italic' : 'normal',
                }}
              />
            </div>

            {/* Download button */}
            <div style={{ marginTop: '0.25rem' }}>
              <button style={btnPrimary} onClick={handleDownloadQrPdf}>{t('downloadQrPdf')}</button>
            </div>
          </div>

          {/* Right column: Live preview */}
          <div style={{ flex: '0 0 auto' }}>
            <label style={{ ...labelStyle, marginBottom: '0.6rem' }}>Preview{savedQrForPreview ? '' : ' (unsaved)'}</label>
            <div style={{
              backgroundColor: '#fff',
              border: `1px solid ${theme?.colors?.border || '#ccc'}`,
              borderRadius: 6,
              padding: 16,
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 160,
              minHeight: 200,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}>
              {previewContent}
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* ── MENU PREVIEW ──────────────────────────────────────── */}
      <CollapsibleSection theme={theme}
        title={`👁 ${t('menuPreview')}`}
        isOpen={showMenuPreview}
        onToggle={() => setShowMenuPreview(!showMenuPreview)}
      >
        {(() => {
          const cols = parseInt(menuColumns) || 1;
          const innerW = cols > 1 ? 1200 : 720;
          const scale = 0.5;
          const previewLang = menuDefaultLanguage || 'en';

          return (
            <div style={{
              border: `1px solid ${theme?.colors?.border || '#ccc'}`,
              borderRadius: theme?.borderRadius || 6,
              maxHeight: 400,
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '0.75rem',
            }}>
              <div ref={el => {
                if (!el) return;
                requestAnimationFrame(() => {
                  const realH = el.getBoundingClientRect().height;
                  const layoutH = el.scrollHeight;
                  el.style.marginBottom = (realH - layoutH) + 'px';
                });
              }} style={{
                width: innerW,
                transformOrigin: 'top left',
                transform: `scale(${scale})`,
              }}>
                {buildMenuElement(previewLang)}
              </div>
            </div>
          );
        })()}
      </CollapsibleSection>

      {/* ── PUBLIC MENU LINK ──────────────────────────────────── */}
      <div style={{
        background: theme?.colors?.surface || '#fff',
        borderRadius: theme?.borderRadius || 8,
        boxShadow: theme?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '1.5rem',
        padding: '1rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        flexWrap: 'wrap',
      }}>
        <div>
          <span style={{ fontWeight: 'bold', color: theme?.colors?.text || '#000', fontSize: '1rem' }}>
            🔗 Public Menu
          </span>
          <span style={{ marginLeft: '0.75rem', fontSize: '0.83rem', color: theme?.colors?.textSecondary || '#888', fontFamily: 'monospace' }}>
            {menuUrl}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <a
            href={menuUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              ...btnPrimary,
              textDecoration: 'none',
              display: 'inline-block',
              whiteSpace: 'nowrap',
            }}
          >
            Preview Menu ↗
          </a>
          {['en', 'es'].map(lang => (
            <button
              key={lang}
              style={{ ...btnPrimary, whiteSpace: 'nowrap', opacity: exportingPdf ? 0.7 : 1, cursor: exportingPdf ? 'default' : 'pointer' }}
              onClick={() => handleExportMenuPdf(lang)}
              disabled={!!exportingPdf}
            >
              {exportingPdf === lang ? 'Generating…' : `Export PDF (${lang.toUpperCase()}) ↓`}
            </button>
          ))}
        </div>
      </div>

      {/* ── AUTO-SAVE INDICATOR ───────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', paddingTop: '0.5rem', minHeight: '2rem' }}>
        {saveStatus === 'saving' && (
          <span style={{ fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#888' }}>
            {t('saving') || 'Saving...'}
          </span>
        )}
        {saveStatus === 'saved' && (
          <span style={{ fontSize: '0.85rem', color: theme?.colors?.success || '#4caf50', fontWeight: 600 }}>
            ✓ {t('settingsSaved') || 'Saved'}
          </span>
        )}
        {saveStatus === 'error' && (
          <span style={{ fontSize: '0.85rem', color: theme?.colors?.error || '#c62828', fontWeight: 600 }}>
            {t('error') || 'Error saving'}
          </span>
        )}
      </div>
    </div>
  );
}

export default DigitalMenuSettings;
